import type { IntegerAmount } from '@actual-app/core/shared/util';
import type {
  CategoryEntity,
  CategoryGroupEntity,
} from '@actual-app/core/types/models';

import { sobrefinanciado } from './objetivos';

/** Origen especial: «Listo para asignar» (el `from` de `cover-overspending`). */
export const LISTO_PARA_ASIGNAR = 'to-budget';

/**
 * Bloques de orígenes, por orden (H26): 1) «Listo para asignar»; 2) lo que
 * sobra por encima del objetivo; 3) categorías sin objetivo; 4) con
 * objetivo o factura pendiente (se avisa: quitarles dinero las deja cortas).
 */
export type BloqueCubrir = 'listo' | 'sobra' | 'libre' | 'reservado';

export const ORDEN_BLOQUES: readonly BloqueCubrir[] = [
  'listo',
  'sobra',
  'libre',
  'reservado',
];

export type OrigenCubrir = {
  /** Id de la categoría o `LISTO_PARA_ASIGNAR`. */
  id: string;
  /** Nombre de la categoría; `null` para «Listo para asignar». */
  nombre: string | null;
  /** Lo que se puede sacar de aquí (> 0). */
  saldo: IntegerAmount;
  bloque: BloqueCubrir;
  /** Solo en el bloque `reservado`: por qué conviene no tocarla. */
  aviso?: 'objetivo' | 'factura';
};

/** Lo que hace falta de cada categoría para clasificarla como origen. */
export type SaldoOrigen = {
  balance: IntegerAmount;
  goal?: IntegerAmount | null;
  longGoal?: boolean;
  budgeted?: IntegerAmount;
  meta?: IntegerAmount | null;
};

/**
 * Orígenes con los que se puede cubrir una categoría en rojo, por bloques
 * (`ORDEN_BLOQUES`) y, dentro de cada bloque, de más a menos saldo. Del
 * bloque «sobra» solo se ofrece lo que pasa del objetivo. Fuera: ingresos,
 * ocultas (o de un grupo oculto), borradas, la propia categoría y las que
 * no tienen nada (> 0) que dar.
 */
export function origenesParaCubrir({
  grupos,
  saldos,
  toBudget,
  destino,
  pendientes,
}: {
  grupos: readonly CategoryGroupEntity[];
  saldos: ReadonlyMap<string, SaldoOrigen | undefined>;
  toBudget: IntegerAmount | null | undefined;
  destino: string;
  /** Categorías con factura de este mes aún sin pagar. */
  pendientes?: ReadonlySet<string>;
}): OrigenCubrir[] {
  const categorias: OrigenCubrir[] = [];
  for (const grupo of grupos) {
    if (grupo.is_income || grupo.hidden || grupo.tombstone) {
      continue;
    }
    for (const c of grupo.categories ?? []) {
      if (c.id === destino || c.is_income || c.hidden || c.tombstone) {
        continue;
      }
      const d = saldos.get(c.id);
      const saldo = d?.balance ?? 0;
      if (!d || saldo <= 0) {
        continue;
      }
      const conObjetivo = d.goal != null || d.meta != null;
      const sobra = conObjetivo
        ? sobrefinanciado({
            goal: d.goal ?? null,
            longGoal: d.longGoal ?? false,
            budgeted: d.budgeted ?? 0,
            balance: saldo,
            spent: 0,
            meta: d.meta,
          })
        : 0;
      if (sobra > 0) {
        categorias.push({
          id: c.id,
          nombre: c.name,
          saldo: sobra,
          bloque: 'sobra',
        });
      } else if (pendientes?.has(c.id)) {
        categorias.push({
          id: c.id,
          nombre: c.name,
          saldo,
          bloque: 'reservado',
          aviso: 'factura',
        });
      } else if (conObjetivo) {
        categorias.push({
          id: c.id,
          nombre: c.name,
          saldo,
          bloque: 'reservado',
          aviso: 'objetivo',
        });
      } else {
        categorias.push({ id: c.id, nombre: c.name, saldo, bloque: 'libre' });
      }
    }
  }
  // `sort` es estable: a igual bloque y saldo se respeta el orden del Plan.
  categorias.sort(
    (a, b) =>
      ORDEN_BLOQUES.indexOf(a.bloque) - ORDEN_BLOQUES.indexOf(b.bloque) ||
      b.saldo - a.saldo,
  );

  return toBudget != null && toBudget > 0
    ? [
        {
          id: LISTO_PARA_ASIGNAR,
          nombre: null,
          saldo: toBudget,
          bloque: 'listo',
        },
        ...categorias,
      ]
    : categorias;
}

/** Orígenes agrupados por bloque (sin bloques vacíos), para los encabezados. */
export function bloquesCubrir(
  origenes: readonly OrigenCubrir[],
): { bloque: BloqueCubrir; origenes: OrigenCubrir[] }[] {
  return ORDEN_BLOQUES.map(bloque => ({
    bloque,
    origenes: origenes.filter(o => o.bloque === bloque),
  })).filter(b => b.origenes.length > 0);
}

/**
 * «Cubrir las N desde Listo para asignar» (H25): sube lo asignado de cada
 * categoría en rojo justo lo que le falta, en un solo `budget-amounts` (un
 * único deshacer). `null` si no hay ninguna o si «Listo para asignar» no
 * alcanza para todas.
 */
export function cubrirTodasDesdeListo({
  enRojo,
  toBudget,
}: {
  enRojo: readonly {
    id: string;
    balance: IntegerAmount;
    budgeted: IntegerAmount;
  }[];
  toBudget: IntegerAmount | null | undefined;
}): {
  total: IntegerAmount;
  amounts: { category: string; amount: IntegerAmount }[];
} | null {
  const enRojoDeVerdad = enRojo.filter(c => c.balance < 0);
  if (enRojoDeVerdad.length === 0 || toBudget == null) {
    return null;
  }
  const total = enRojoDeVerdad.reduce((s, c) => s - c.balance, 0);
  if (total > toBudget) {
    return null;
  }
  return {
    total,
    amounts: enRojoDeVerdad.map(c => ({
      category: c.id,
      amount: c.budgeted - c.balance,
    })),
  };
}

/** Categorías de gasto candidatas a origen (para leer su saldo del mes). */
export function categoriasCandidatas(
  grupos: readonly CategoryGroupEntity[],
): CategoryEntity[] {
  return grupos
    .filter(g => !g.is_income && !g.hidden && !g.tombstone)
    .flatMap(g => g.categories ?? [])
    .filter(c => !c.is_income && !c.hidden && !c.tombstone);
}

/** Lo que falta en una categoría según su saldo (0 si no está en rojo). */
export function faltaPorCubrir(
  balance: IntegerAmount | null | undefined,
): IntegerAmount {
  return balance != null && balance < 0 ? -balance : 0;
}

/** Importe a mover: todo lo que falta, o lo que tenga el origen si no llega. */
export function importeCubrir(
  falta: IntegerAmount,
  saldoOrigen: IntegerAmount,
): IntegerAmount {
  return Math.max(0, Math.min(falta, saldoOrigen));
}

export type PasoTrasCubrir = 'quedarse' | 'lista' | 'cerrar';

/**
 * Qué hace la hoja después de cubrir: si no llegó, se queda en «¿De dónde
 * sale?»; si cubrió todo, vuelve a la lista si quedan otras en rojo o se
 * cierra si no queda ninguna.
 */
export function pasoTrasCubrir({
  falta,
  importe,
  otrasEnRojo,
}: {
  falta: IntegerAmount;
  importe: IntegerAmount;
  otrasEnRojo: number;
}): PasoTrasCubrir {
  if (importe < falta) {
    return 'quedarse';
  }
  return otrasEnRojo > 0 ? 'lista' : 'cerrar';
}

export type AccionSaldo = 'cubrir' | 'hoja';

/**
 * Qué abre tocar el Disponible de una fila del Plan en el móvil (H24): la
 * hoja «Cubrir» si está en rojo y la hoja del saldo si no.
 */
export function accionSaldo({
  balance,
}: {
  balance: IntegerAmount | null | undefined;
}): AccionSaldo {
  return balance != null && balance < 0 ? 'cubrir' : 'hoja';
}
